import axios from '../api';

/**
 * Logs frontend events to the backend for debugging
 * Use case: Track user actions that don't reach the server (save clicks, errors, etc.)
 * @param {string} event - Event name (e.g., 'SAVE_CLICKED', 'SAVE_SUCCESS', 'SAVE_ERROR')
 * @param {string} token - Customer token
 * @param {Object} data - Additional data to log
 */
export const logFrontendEvent = async (event, token, data = {}) => {
    try {
        await axios.post('/frontend-log', {
            event,
            token,
            data,
            timestamp: new Date().toISOString(),
            userAgent: navigator.userAgent
        });
    } catch (e) {
        // Silent fail - logging should never break the app
        console.warn('Frontend logging failed:', e);
    }
};

/**
 * Fetches and organizes customer meal plan data
 * Use case: Initial meal plan loading when customer accesses their personalized meal plan page
 * @param {string} token - Customer's unique token for accessing their meal plan
 * @returns {Object} Object containing customerDetails, MealsByDate by delivery date, and sortedDates
 */
export const fetchMealPlan = async (token) => {
    const response = await axios.get(`/orders/${token}`);
    // Group orders by delivery date
    const MealsByDate = {};

    response.data.orders.forEach(order => {
        const deliveryDate = order.deliveryDate || 'No Date';
        if (!MealsByDate[deliveryDate]) {
            MealsByDate[deliveryDate] = [];
        }
        MealsByDate[deliveryDate].push(order);
    });

    // Sort orders within each delivery date by quantity (highest first)
    Object.keys(MealsByDate).forEach(deliveryDate => {
        MealsByDate[deliveryDate].sort((a, b) => b.quantity - a.quantity);
    });

    // Sort delivery dates
    delete response.data.orders;
    return {
        customerDetails: response.data,
        MealsByDate,
    };
};

/**
 * Updates the quantity of an existing dish in the meal plan
 * Use case: When a customer wants more/fewer servings of a dish they already have
 * @param {string} token - Customer's unique token
 * @param {string} recordId - ID of the existing order record
 * @param {number} newQuantity - New quantity for the dish
 * @param {string} itemName - Name of the item being updated
 * @returns {Object} API response from quantity update
 */
export const updateQuantity = async (token, recordId, newQuantity, itemName) => {
    return await axios.patch(`/orders/${token}/quantity`, {
        recordId,
        newQuantity,
        itemName
    });
};

/**
 * Adds a completely new dish to the meal plan
 * Use case: When a customer wants to add a new dish they don't currently have
 * @param {string} token - Customer's unique token
 * @param {string} dishId - ID of the dish to add
 * @param {string} mealType - Type of meal (e.g., 'lunch', 'dinner')
 * @param {string} requestedDeliveryDate - Date when customer wants the dish delivered
 * @returns {Object} API response from dish addition
 */
export const addDish = async (token, dishId, mealType, requestedDeliveryDate, subscriptions) => {
    return await axios.post(`/orders/${token}/add-dish`, {
        dishId,
        mealType,
        requestedDeliveryDate,
        subscriptions
    });
};

/**
 * Replaces the protein in an existing dish
 * Use case: When a customer wants to substitute the protein in a dish they already have (e.g., chicken to beef)
 * @param {string} token - Customer's unique token
 * @param {string} recordId - ID of the existing order record
 * @param {string} newProteinId - ID of the new protein to use
 * @param {string} oldProteinId - ID of the current protein being replaced
 * @param {number} upgradePrice - Additional cost for protein upgrade (default: 0)
 * @returns {Object} API response from protein replacement
 */
export const replaceProtein = async (token, order, newProteinId, oldProteinId, upgradePrice = 0) => {
    return await axios.patch(`/orders/${token}/replace-protein`, {
        order,
        newProteinId,
        oldProteinId,
        upgradePrice
    });
};

/**
 * Adds or removes a vegetable from an existing dish
 * Use case: When a customer wants to add vegetables to a dish or remove vegetables they don't want
 * @param {string} token - Customer's unique token
 * @param {string} recordId - ID of the existing order record
 * @param {string} veggieId - ID of the vegetable to toggle
 * @param {boolean} shouldActivate - true to add vegetable, false to remove
 * @returns {Object} API response from vegetable toggle
 */
export const toggleVeggie = async (token, order, targetVeggieId, isActive) => {
    return await axios.patch(`/orders/${token}/toggle-veggie`, {
        order,
        targetVeggieId,
        isActive
    });
};

/**
 * Replaces the sauce in an existing dish
 * Use case: When a customer wants to change the sauce in a dish they already have (e.g., teriyaki to garlic sauce)
 * @param {string} token - Customer's unique token
 * @param {string} recordId - ID of the existing order record
 * @param {string} newSauceId - ID of the new sauce to use
 * @param {string} oldSauceId - ID of the current sauce being replaced
 * @returns {Object} API response from sauce replacement
 */
export const replaceSauce = async (token, order, newSauceId, oldSauceId) => {
    return await axios.patch(`/orders/${token}/replace-sauce`, {
        order,
        newSauceId,
        oldSauceId
    });
};

/**
 * Replaces the starch in an existing dish
 * Use case: When a customer wants to change the starch in a dish they already have (e.g., rice to quinoa)
 * @param {string} token - Customer's unique token
 * @param {string} recordId - ID of the existing order record
 * @param {string} starchId - ID of the new starch to use
 * @param {string} oldStarchId - ID of the current starch being replaced
 * @returns {Object} API response from starch replacement
 */
export const toggleStarch = async (token, order, starchId, oldStarchId) => {
    return await axios.patch(`/orders/${token}/toggle-starch`, {
        order,
        starchId,
        oldStarchId
    });
};

/**
 * Adds or removes a garnish from an existing dish
 * Use case: When a customer wants to add garnishes for flavor or remove garnishes they don't like
 * @param {string} token - Customer's unique token
 * @param {string} recordId - ID of the existing order record
 * @param {string} garnishId - ID of the garnish to toggle
 * @param {boolean} shouldActivate - true to add garnish, false to remove
 * @returns {Object} API response from garnish toggle
 */
export const toggleGarnish = async (token, order, garnishId, isActive) => {
    return await axios.patch(`/orders/${token}/toggle-garnish`, {
        order,
        garnishId,
        isActive
    });
};

/**
 * Skips delivery for a specific date by deleting all orders
 * Use case: When a customer needs to skip delivery for a specific date (vacation, travel, etc.)
 * @param {string} token - Customer's unique token
 * @param {string} deliveryDate - The delivery date to skip
 * @param {string} nextDeliveryDate - Optional alternative delivery date
 * @returns {Object} API response from skip delivery
 */
export const skipDelivery = async (token, deliveryDate, nextDeliveryDate, subscriptionSummary, customerName) => {
    return await axios.delete(`/orders/${token}/skip-delivery/${deliveryDate}`, {
        data: nextDeliveryDate ? { nextDeliveryDate, subscriptionSummary, customerName } : { subscriptionSummary, customerName }
    });
};

/**
 * Resumes delivery for a previously skipped date
 * Use case: When a customer changes their mind and wants to receive delivery for a previously skipped date
 * @param {string} token - Customer's unique token
 * @param {string} deliveryDate - The delivery date to resume
 * @returns {Object} API response from resume delivery
 */
export const resumeDelivery = async (token, deliveryDate) => {
    return await axios.post(`/orders/${token}/resume-delivery/${deliveryDate}`);
};

/**
 * Batch saves all pending changes for a delivery date
 * Use case: When user clicks "Save Changes" or completes payment/refund for a date
 * @param {string} token - Customer's unique token
 * @param {string} deliveryDate - The delivery date for these changes
 * @param {Array} changes - Array of change objects to process
 * @param {Object} billingChanges - Object containing billing details for the delivery date
 * @returns {Object} API response with results for each change
 */
export const batchSave = async (token, deliveryDate, changes, billingChanges) => {
    return await axios.post(`/orders/${token}/batch-save`, {
        deliveryDate,
        changes,
        billingChanges
    });
};

/**
 * Stores pending changes server-side before Stripe checkout redirect
 * @param {string} token - Customer's unique token
 * @param {Object} changesPerDate - { [deliveryDate]: { changes: [...] } }
 * @param {Object} billingChanges - Billing changes object
 * @returns {Object} { success, pendingId }
 */
export const storePendingChanges = async (token, changesPerDate, billingChanges) => {
    const response = await axios.post('/pending-changes', {
        token,
        changesPerDate,
        billingChanges
    });
    return response.data;
};

/**
 * Creates a Stripe checkout session for payment
 * @param {string} pendingId - ID from storePendingChanges
 * @param {Array} lineItems - [{ deliveryDate, amount, description }]
 * @param {string} token - Customer's unique token
 * @returns {Object} { success, url, sessionId }
 */
export const createCheckoutSession = async (pendingId, lineItems, token, stripeCustomerId) => {
    const response = await axios.post('/create-checkout-session', {
        pendingId,
        lineItems,
        token,
        stripeCustomerId: stripeCustomerId || undefined
    });
    return response.data;
};

/**
 * Checks if pending changes have been processed after Stripe payment
 * @param {string} pendingId - ID from storePendingChanges
 * @returns {Object} { success, processed }
 */
export const checkPendingStatus = async (pendingId) => {
    const response = await axios.get(`/pending-changes/${pendingId}/status`);
    return response.data;
};

/**
 * Fetches prior payment totals grouped by delivery date
 * @param {string} token - Customer's unique token
 * @returns {Object} { success, paymentsByDate: { [date]: totalPaid } }
 */
export const getPaymentHistory = async (token) => {
    const response = await axios.get(`/payment-history/${token}`);
    return response.data;
};

